import React, { useEffect, useState } from "react";

import Loader from "../../../common/Loader";
import { PlatformTeamUserRow } from "../../../common/platformTypes";
import { PlatformUsersTable } from "../../../components/PlatformUsersTable";
import { searchPlatformUsers } from "../../../services/api/Platform";

/**
 * The server matches an email or a platform user id exactly, so only a term
 * shaped like one of those can ever hit: "@" for an address, "-" for the
 * tenant separator every platform id carries (`stub-usr-dev:…`). A Mongo `_id` or
 * a Slack id has neither, so the section stays out of the way entirely
 * rather than rendering an empty table under every Slack-only search.
 */
const isSearchable = (search: string): boolean => {
  const term = search.trim();
  return term.length > 0 && (term.includes("@") || term.includes("-"));
};

type PlatformTeamUsersPanelProps = {
  search: string;
  enabled: boolean;
};

export const PlatformTeamUsersPanel: React.FunctionComponent<
  PlatformTeamUsersPanelProps
> = ({ search, enabled }) => {
  const [rows, setRows] = useState<PlatformTeamUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = enabled && isSearchable(search);

  useEffect(() => {
    if (!active) {
      setRows([]);
      setError(null);
      return () => {};
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    searchPlatformUsers(search)
      .then((result) => {
        if (!cancelled) {
          setRows(result);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [search, active]);

  if (!active) {
    return null;
  }

  return (
    <>
      <h5 className="text-muted mt-3 mb-0">Platform Team Identities</h5>
      <div className="card bg-dark mt-2">
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="alert alert-danger mb-0">{error}</div>
        ) : (
          <PlatformUsersTable rows={rows} />
        )}
      </div>
    </>
  );
};
